import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Sparkle, Loader2, Check } from "lucide-react";

const ACCENT = "#4FA3D9";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INPUT_CLS =
  "w-full rounded-xl border border-white/12 bg-black/30 px-4 py-3 text-base text-white placeholder:text-white/30 focus:border-[#4FA3D9] focus:outline-none";

// Universal feedback renderer. All visible questions/options/copy come from the simulation's
// feedback configuration, resolved server-side from the canonical session (or a `config` prop).
export const FeedbackForm = ({ sessionId, prefillEmail, config: configProp } = {}) => {
  const [config, setConfig] = useState(configProp || null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [optIn, setOptIn] = useState(false);
  const [contactError, setContactError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [missingRequired, setMissingRequired] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (configProp || !sessionId) return;
    let cancelled = false;
    setLoadFailed(false);
    fetch(`${API}/feedback/config?session_id=${encodeURIComponent(sessionId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        if (d?.config) { setConfig(d.config); setAlreadySubmitted(Boolean(d.already_submitted)); }
        else setLoadFailed(true);
      })
      .catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => { cancelled = true; };
  }, [sessionId, configProp, retryKey]);

  // Pre-fill the optional email if the user already sent themselves the report this flow.
  useEffect(() => {
    if (prefillEmail) setEmail((cur) => (cur ? cur : prefillEmail));
  }, [prefillEmail]);

  const setAnswer = (qid, value) => setAnswers((a) => ({ ...a, [qid]: value }));
  const hasContact = Boolean(email.trim()) || Boolean(phone.trim());

  useEffect(() => {
    if (contactError && (!optIn || hasContact)) setContactError(false);
  }, [optIn, hasContact, contactError]);

  const isAnswered = (v) => v !== undefined && v !== null && v !== "" && !(typeof v === "string" && !v.trim());

  useEffect(() => {
    if (missingRequired.length) setMissingRequired((m) => m.filter((qid) => !isAnswered(answers[qid])));
  }, [answers]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    // Required status is read from the simulation's feedback config — never hardcoded here.
    const missing = config.questions.filter((q) => q.required && !isAnswered(answers[q.question_id])).map((q) => q.question_id);
    if (missing.length) {
      setMissingRequired(missing);
      return;
    }
    if (optIn && !hasContact) {
      setContactError(true);
      return;
    }
    setSubmitting(true);
    setError(false);
    try {
      const body = {};
      for (const q of config.questions) {
        const v = answers[q.question_id];
        if (v !== undefined && v !== null && v !== "") body[q.question_id] = v;
      }
      const res = await fetch(`${API}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId || undefined,
          answers: body,
          email,
          phone,
          future_simulations_opt_in: optIn,
        }),
      });
      if (!res.ok) throw new Error("failed");
      const data = await res.json().catch(() => ({}));
      // created === false: feedback already existed for this attempt (stale/racing client).
      if (data?.created === false) setAlreadySubmitted(true);
      else setDone(true);
    } catch (e) {
      setError(true);
    }
    setSubmitting(false);
  };

  if (!config) {
    if (!loadFailed) return null;
    return (
      <div className="mt-14 border-t border-white/10 pt-10" data-testid="feedback-load-failed">
        <p className="text-sm text-white/55">
          Feedback couldn't load. Please try again.{" "}
          <button
            onClick={() => setRetryKey((k) => k + 1)}
            data-testid="feedback-retry"
            className="font-medium underline underline-offset-4 transition-opacity hover:opacity-80"
            style={{ color: ACCENT }}
          >
            Try again
          </button>
        </p>
      </div>
    );
  }

  if (alreadySubmitted && !done) {
    return (
      <div className="mt-12 rounded-3xl border border-white/10 bg-white/[0.04] p-8 text-center backdrop-blur-sm" data-testid="feedback-already-submitted">
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full" style={{ background: ACCENT }}>
          <Check size={22} className="text-white" />
        </div>
        <p className="font-display text-2xl font-semibold text-white">Thank you for helping shape Adaptience™.</p>
        <p className="mt-3 text-base italic text-white/55">Your feedback for this practice has already been submitted.</p>
      </div>
    );
  }

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-12 rounded-3xl border border-white/10 bg-white/[0.04] p-8 text-center backdrop-blur-sm"
        data-testid="feedback-thanks"
      >
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full" style={{ background: ACCENT }}>
          <Check size={22} className="text-white" />
        </div>
        <p className="font-display text-2xl font-semibold text-white">{config.success?.title}</p>
        {config.success?.supporting && (
          <p className="mt-3 text-base italic text-white/55">{config.success.supporting}</p>
        )}
      </motion.div>
    );
  }

  const contact = config.contact || {};
  const optInCfg = config.future_simulations_opt_in;

  return (
    <div className="mt-14 border-t border-white/10 pt-10" data-testid="feedback-form">
      {config.overline && (
        <div className="mb-2 flex items-center gap-2" style={{ color: ACCENT }}>
          <Sparkle size={15} />
          <span className="text-xs font-semibold tracking-[0.12em]">{config.overline}</span>
        </div>
      )}
      {(config.intro || config.emphasis) && (
        <p className="mb-8 text-base leading-relaxed text-white/60">
          {config.intro}
          {config.emphasis && <span className="mt-2 block font-medium text-white/80">{config.emphasis}</span>}
        </p>
      )}

      <div className="space-y-7">
        {config.questions.map((q) => (
          <Question key={q.question_id} q={q} value={answers[q.question_id]} onChange={(v) => setAnswer(q.question_id, v)} missing={missingRequired.includes(q.question_id)} />
        ))}

        <div>
          <label className="mb-1 block text-sm font-medium text-white/80">
            {contact.heading} {contact.optional_label && <span className="text-white/45">{contact.optional_label}</span>}
          </label>
          {contact.supporting && <p className="mb-3 text-xs text-white/45">{contact.supporting}</p>}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label={contact.email_label || "Email"}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="feedback-email"
                placeholder={contact.email_placeholder || ""}
                className={INPUT_CLS}
              />
            </Field>
            <Field label={contact.phone_label || "Phone / WhatsApp"}>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="feedback-phone"
                placeholder={contact.phone_placeholder || ""}
                className={INPUT_CLS}
              />
            </Field>
          </div>
        </div>

        {optInCfg && (
          <div>
            <button onClick={() => setOptIn((v) => !v)} data-testid="feedback-optin" className="flex items-center gap-3 text-left">
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition-colors"
                style={optIn ? { background: ACCENT, borderColor: ACCENT } : { borderColor: "rgba(255,255,255,0.25)" }}
              >
                {optIn && <Check size={15} className="text-white" />}
              </span>
              <span className="text-sm text-white/75">{optInCfg.label}</span>
            </button>
            {contactError && (
              <p className="mt-3 text-sm text-red-400/80" data-testid="feedback-contact-required">
                {optInCfg.contact_required_message}
              </p>
            )}
          </div>
        )}

        <button
          onClick={submit}
          disabled={submitting}
          data-testid="feedback-submit"
          className="mt-2 inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-[0.16em] text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ background: ACCENT }}
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
          {submitting ? config.submitting_label || "Sending…" : config.submit_label || "Submit feedback"}
        </button>
        {error && (
          <p className="text-sm text-red-400/80" data-testid="feedback-error">
            {config.error_message || "Something went wrong sending your feedback. Please try again."}
          </p>
        )}
      </div>
    </div>
  );
};

const RequiredNote = ({ tid }) => (
  <p className="mt-2.5 text-sm text-red-400/80" data-testid={`${tid}-required`}>Please answer this question to submit your feedback.</p>
);

const Question = ({ q, value, onChange, missing }) => {
  const tid = `feedback-q-${q.question_id}`;
  if (q.type === "rating") {
    const min = q.min ?? 1;
    const max = q.max ?? 5;
    const scale = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    return (
      <div>
        <label className="mb-3 block text-sm font-medium text-white/80">{q.label}</label>
        <div className="flex gap-2.5" data-testid={tid}>
          {scale.map((n) => (
            <button
              key={n}
              onClick={() => onChange(n)}
              data-testid={`${tid}-${n}`}
              className="flex h-11 w-11 items-center justify-center rounded-full border text-base font-semibold transition-colors"
              style={(value || 0) >= n
                ? { background: ACCENT, borderColor: ACCENT, color: "#fff" }
                : { borderColor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.7)" }}
            >
              {n}
            </button>
          ))}
        </div>
        {(q.min_label || q.max_label) && (
          <p className="mt-2.5 text-xs text-white/45" data-testid={`${tid}-scale-labels`}>
            {q.min_label && <span>{min} — {q.min_label}</span>}
            {q.min_label && q.max_label && <span className="mx-2">·</span>}
            {q.max_label && <span>{max} — {q.max_label}</span>}
          </p>
        )}
        {missing && <RequiredNote tid={tid} />}
      </div>
    );
  }
  if (q.type === "single_select") {
    return (
      <div>
        <label className="mb-3 block text-sm font-medium text-white/80">{q.label}</label>
        <div className="flex flex-wrap gap-3" data-testid={tid}>
          {q.options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onChange(opt.value)}
              data-testid={`${tid}-${opt.value}`}
              className="rounded-full border px-6 py-2.5 text-sm font-medium transition-colors"
              style={value === opt.value
                ? { background: ACCENT, borderColor: ACCENT, color: "#fff" }
                : { borderColor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.7)" }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {missing && <RequiredNote tid={tid} />}
      </div>
    );
  }
  if (q.type === "text") {
    return (
      <Field label={q.label}>
        {q.multiline ? (
          <textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            rows={q.rows || 3}
            data-testid={tid}
            placeholder={q.placeholder || ""}
            className={`${INPUT_CLS} resize-none`}
          />
        ) : (
          <input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={tid}
            placeholder={q.placeholder || ""}
            className={INPUT_CLS}
          />
        )}
        {missing && <RequiredNote tid={tid} />}
      </Field>
    );
  }
  return null;
};

const Field = ({ label, children }) => (
  <div>
    <label className="mb-2 block text-sm font-medium text-white/80">{label}</label>
    {children}
  </div>
);

export default FeedbackForm;
